import React, { useCallback, useEffect, useState } from 'react';
import Button from 'components/Button';
import RefreshButton from 'components/RefreshButton';
import Toast from 'components/Toast';
import { useActiveCompanyStore } from 'stores/activeCompanyStore';
import { useCompanyServiceQueries } from 'services/company';
import { Company } from 'types/company';
import styles from './CompanyUsers.module.scss';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from 'utils/constants';
import { ArrowLeft } from 'lucide-react';
import { useUserProfileStore } from 'stores/userProfileStore';

interface CompanyUser {
  id: string;
  email: string;
  username?: string;
  role?: 'owner' | 'member';
  owned?: boolean;
}

function CompanyUsers() {
  const activeCompanyId = useActiveCompanyStore((s) => s.activeCompanyId);
  const companyService = useCompanyServiceQueries();
  const navigate = useNavigate();
  const profile = useUserProfileStore((s) => s.profile);

  const [users, setUsers] = useState<CompanyUser[]>([]);
  const [company, setCompany] = useState<Company | null>(null);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [reloading, setReloading] = useState(false);
  const [linking, setLinking] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(
    async (isReload = false) => {
      if (!activeCompanyId) return;
      setError(null);
      if (isReload) setReloading(true);
      else setLoading(true);
      try {
        const [companyUsers, companyDetails] = await Promise.all([
          companyService.getCompanyUsers(activeCompanyId),
          companyService.getCompanyById(activeCompanyId),
        ]);
        setUsers(companyUsers);
        setCompany(companyDetails);
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to load users';
        setError(message);
        setToast({ message, type: 'error' });
      } finally {
        if (isReload) setReloading(false);
        else setLoading(false);
      }
    },
    [activeCompanyId, companyService],
  );

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const handleLink = useCallback(async () => {
    if (!activeCompanyId || !email.trim()) {
      setToast({
        message: 'Please enter an email to link.',
        type: 'error',
      });
      return;
    }
    setLinking(true);
    try {
      await companyService.linkUserToCompany(activeCompanyId, email.trim());
      setToast({ message: 'User linked successfully.', type: 'success' });
      setEmail('');
      await loadData(true);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to link user';
      setToast({ message, type: 'error' });
    } finally {
      setLinking(false);
    }
  }, [activeCompanyId, email, companyService, loadData]);

  const handleUnlink = useCallback(
    async (userId: string) => {
      if (!activeCompanyId) return;
      try {
        await companyService.unlinkCompanyUser(activeCompanyId, userId);
        setToast({ message: 'User unlinked successfully.', type: 'success' });
        await loadData(true);
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to unlink user';
        setToast({ message, type: 'error' });
      }
    },
    [activeCompanyId, companyService, loadData],
  );

  return (
    <div className={styles.container}>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <button
            type="button"
            className={styles.backButton}
            onClick={() => navigate(ROUTES.COMPANIES.ROOT)}
            aria-label="Back to companies"
          >
            <ArrowLeft size={18} />
          </button>
          <h1 className={styles.title}>
            Company Users {company ? `- ${company.name}` : ''}
          </h1>
        </div>
        <div className={styles.actions}>
          <RefreshButton
            onClick={() => void loadData(true)}
            loading={reloading}
          />
        </div>
      </div>

      <div className={styles.formRow}>
        <div className={styles.inputGroup}>
          <label htmlFor="link-email">Add user by email</label>
          <input
            id="link-email"
            type="email"
            placeholder="user@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={linking}
          />
        </div>
        <Button onClick={() => void handleLink()} disabled={linking}>
          {linking ? 'Linking...' : 'Link User'}
        </Button>
      </div>

      {error && (
        <div className={styles.errorState}>
          <p>{error}</p>
        </div>
      )}

      {loading ? (
        <div className={styles.loadingState}>Loading users...</div>
      ) : users.length === 0 ? (
        <div className={styles.emptyState}>
          <p>No users linked to this company.</p>
        </div>
      ) : (
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Email</th>
                <th>Name</th>
                <th>Role</th>
                <th className={styles.actionsCell}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => {
                const isUserOwner =
                  user.role === 'owner' || user.owned === true;
                const isCurrentUser = profile?.email === user.email;
                const canRemove = !isUserOwner && !isCurrentUser;
                const roleLabel = isUserOwner ? 'Owner' : 'Member';
                return (
                  <tr key={user.id}>
                    <td>{user.email}</td>
                    <td>{user.username || '-'}</td>
                    <td>
                      <span
                        className={`${styles.roleBadge} ${
                          roleLabel === 'Member' ? styles.memberBadge : ''
                        }`}
                      >
                        {roleLabel}
                      </span>
                    </td>
                    <td className={styles.actionsCell}>
                      {canRemove && (
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => void handleUnlink(user.id)}
                        >
                          Remove
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default CompanyUsers;
